/**
 * Story `fe-be-monteur-s5b-installeerbaar` (EPIC-021, S021.5b) §1.2 / AC 5 — een lazy scherm
 * dat na een deploy niet meer bestaat.
 *
 * Een tabblad dat vóór een deploy geopend is, kent de OUDE bestandsnamen (met hash). Vraagt
 * het daarna een lazy scherm op, dan bestaat dat bestand op de server niet meer. Dan:
 *  1. **één keer automatisch herladen** — de nieuwe `index.html` kent de nieuwe namen;
 *  2. faalt het daarna opnieuw: géén herlaad meer, maar de melding "Er is een nieuwe versie
 *     van CARculate. Vernieuw de pagina." met een knop. Nooit een lus.
 *
 * <b>Hoe "één keer" wordt bijgehouden</b> (review H-2): een vlag in `sessionStorage` met de
 * sleutel van het scherm dat faalde en het tijdstip. Een tweede fout op HETZELFDE scherm, of
 * binnen {@link RELOAD_WINDOW_MS} na de vorige herlaad, toont de melding. Laadt dat scherm
 * daarna wél, dan gaat de vlag weg — zodat een tweede deploy later in hetzelfde tabblad
 * weer één automatische poging krijgt (TC-FE-CHK-07). Een geslaagd laden van een ÁNDER
 * scherm wist de vlag niet: anders lust "Chrome laadt, het dashboard faalt" eindeloos.
 *
 * <b>Zonder werkende opslag</b> (privé-venster, geblokkeerd): nooit herladen, meteen de
 * melding (TC-FE-CHK-05). Zonder vlag is er geen bescherming tegen een lus.
 *
 * <b>Herkenning</b> (review A-9): niet op de tekst van de foutmelding. Elke afwijzing van de
 * `import()` van een lazy scherm telt, en daarnaast het `vite:preloadError`-event (Vite vuurt
 * dat wanneer een voor-geladen afhankelijkheid niet laadt — ook de variant waarin de server
 * `index.html` met 200 teruggeeft, die de browser als MIME-fout meldt).
 *
 * PB-5 ("nooit vanzelf herladen") gaat over een NIEUWE versie terwijl de app werkt; dit is
 * foutherstel — het scherm kan toch niet tonen. De fotowachtrij staat in IndexedDB en
 * overleeft de herlaad (review H-3).
 */

/** De sleutel in `sessionStorage`. */
export const CHUNK_RELOAD_FLAG = 'carculate:chunk-reload';

/** Binnen dit venster na een automatische herlaad volgt nooit een tweede (anti-lus). */
export const RELOAD_WINDOW_MS = 60_000;

/** De melding na een tweede mislukte poging (AC 5). */
export const CHUNK_RELOAD_MESSAGE = 'Er is een nieuwe versie van CARculate. Vernieuw de pagina.';

/** Wat de afhandeling van één laadfout besluit. */
export type ChunkFailureDecision = 'reload' | 'notice';

interface ReloadFlag {
    key: string;
    at: number;
}

/** De omgeving van de afhandeling (injecteerbaar voor de tests). */
export interface ChunkReloadEnv {
    /** `sessionStorage` (of `null` als hij er niet is). Elke aanroep mag gooien. */
    storage: () => Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null;
    reload: () => void;
    now: () => number;
}

/** De echte omgeving: `sessionStorage`, `location.reload()` en de klok. */
export function browserChunkReloadEnv(): ChunkReloadEnv {
    return {
        storage: () => (typeof window === 'undefined' ? null : window.sessionStorage),
        reload: () => window.location.reload(),
        now: () => Date.now(),
    };
}

/**
 * De afhandeling, als object met eigen toestand: één instantie per pagina (zie
 * {@link chunkReloader}). `reloadScheduled` zorgt dat twee fouten tegelijk (twee schermen
 * die vlak na elkaar laden) maar één herlaad geven (TC-FE-CHK-10).
 */
export function createChunkReloader(env: ChunkReloadEnv) {
    let reloadScheduled = false;

    function readFlag(): ReloadFlag | null | 'unavailable' {
        try {
            const storage = env.storage();
            if (!storage) return 'unavailable';
            const raw = storage.getItem(CHUNK_RELOAD_FLAG);
            if (!raw) return null;
            const parsed = JSON.parse(raw) as Partial<ReloadFlag>;
            if (typeof parsed?.key !== 'string' || typeof parsed?.at !== 'number') return null;
            return { key: parsed.key, at: parsed.at };
        } catch {
            return 'unavailable';
        }
    }

    function writeFlag(flag: ReloadFlag): boolean {
        try {
            const storage = env.storage();
            if (!storage) return false;
            storage.setItem(CHUNK_RELOAD_FLAG, JSON.stringify(flag));
            return true;
        } catch {
            return false;
        }
    }

    function clearFlag(): void {
        try {
            env.storage()?.removeItem(CHUNK_RELOAD_FLAG);
        } catch {
            /* opslag onbereikbaar: niets te wissen */
        }
    }

    return {
        /**
         * Een lazy scherm met sleutel `key` laadde niet. Levert `'reload'` (en herlaadt) of
         * `'notice'` (de aanroeper toont de melding).
         */
        onFailure(key: string): ChunkFailureDecision {
            if (reloadScheduled) return 'reload';
            const flag = readFlag();
            if (flag === 'unavailable') return 'notice';
            const now = env.now();
            if (flag && (flag.key === key || now - flag.at < RELOAD_WINDOW_MS)) return 'notice';
            if (!writeFlag({ key, at: now })) return 'notice';
            reloadScheduled = true;
            env.reload();
            return 'reload';
        },

        /** Het scherm met sleutel `key` laadde wél: was dat het scherm van de vlag, dan gaat hij weg. */
        onSuccess(key: string): void {
            const flag = readFlag();
            if (flag && flag !== 'unavailable' && flag.key === key) clearFlag();
        },

        /** De knop "Vernieuwen" in de melding: de vlag weg (een volgende fout krijgt weer één poging) en herladen. */
        reloadByUser(): void {
            clearFlag();
            env.reload();
        },
    };
}

export type ChunkReloader = ReturnType<typeof createChunkReloader>;

let instance: ChunkReloader | null = null;

/** De afhandeling van deze pagina. */
export function chunkReloader(): ChunkReloader {
    if (!instance) instance = createChunkReloader(browserChunkReloadEnv());
    return instance;
}

/** Alleen voor tests: vervang (of reset met `null`) de afhandeling van de pagina. */
export function setChunkReloaderForTests(reloader: ChunkReloader | null): void {
    instance = reloader;
}

/**
 * Luistert naar `vite:preloadError` (review A-9b). De afwijzing van de `import()` zelf vangt
 * `lazyScreen` al; dit vangt de dynamische imports die niet via `lazyScreen` lopen (bv. een
 * bibliotheek die pas bij een knop geladen wordt). Eén luisteraar per pagina.
 */
export function installPreloadErrorListener(target: Window = window): () => void {
    const onPreloadError = () => {
        chunkReloader().onFailure('vite:preloadError');
    };
    target.addEventListener('vite:preloadError', onPreloadError);
    return () => target.removeEventListener('vite:preloadError', onPreloadError);
}
